import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2 } from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { useToast } from '../../../src/contexts/ToastContext';
import { useAuth } from '../../../src/contexts/AuthContext';
import { logUsageEvent } from '../../../src/lib/logger';
import { Rateio } from '../../../types';

interface RegistrarEspelhoModalProps {
  rateio: Rateio;
  ugNome: string;
  onClose: () => void;
  onSaved: () => void;
}

const formatKwh = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Registro manual do espelho de rateio (Seção 7/Tela 4 — entrada manual, já
 * que não há mecanismo de leitura automática de e-mail/anexo). Compara o
 * total informado com o excedente_alocavel_kwh calculado e grava a
 * divergência % (tolerância de 0,5%, Seção 13.4).
 */
const RegistrarEspelhoModal: React.FC<RegistrarEspelhoModalProps> = ({ rateio, ugNome, onClose, onSaved }) => {
  const { addToast } = useToast();
  const { user } = useAuth();
  const [valor, setValor] = useState(rateio.excedente_faturado_kwh != null ? String(rateio.excedente_faturado_kwh) : '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const excedenteFaturado = parseFloat(valor.replace(',', '.'));
    if (!Number.isFinite(excedenteFaturado) || excedenteFaturado < 0) {
      addToast('Informe um valor válido.', 'error');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.rpc('registrar_espelho_rateio', {
        p_rateio_id: rateio.id,
        p_excedente_faturado_kwh: excedenteFaturado,
      });
      if (error) throw error;
      addToast('Espelho registrado com sucesso.', 'success');
      logUsageEvent({ userId: user?.id, modulo: 'gestao', funcionalidade: 'rateio_conciliacao', acao: 'registrar_espelho_rateio', origem: 'acao' });
      onSaved();
    } catch (error: any) {
      console.error('Error registering espelho', error);
      addToast(error?.message || 'Erro ao registrar o espelho.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white font-poppins">Registrar Espelho</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {ugNome} — {rateio.mes_referencia}
            <br />
            Excedente calculado: <strong>{formatKwh(rateio.excedente_alocavel_kwh)} kWh</strong>
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Excedente Faturado pela Distribuidora (kWh)
            </label>
            <input
              type="number"
              step="0.01"
              min={0}
              value={valor}
              onChange={e => setValor(e.target.value)}
              placeholder="0,00"
              className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Valor total do espelho recebido da distribuidora — entrada manual (Seção 7 da spec, sem leitura automática de anexo definida).
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-primary text-white font-medium hover:opacity-90 disabled:opacity-60 flex items-center gap-2"
          >
            {saving && <Loader2 size={16} className="animate-spin" />}
            Salvar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RegistrarEspelhoModal;
